import { useEffect, useState } from 'react';
import { logger } from '../../../shared/utils/logger.js';
import type { EmployeeRoleRow, EmployeeRoleWriteInput } from '../api/employees.api';

type RoleEditorModalProps = {
  open: boolean;
  initial?: EmployeeRoleRow | null;
  submitting: boolean;
  errorMessage?: string;
  onClose: () => void;
  onSubmit: (input: EmployeeRoleWriteInput) => void;
};

const TITLE_FOR = (initial?: EmployeeRoleRow | null): string =>
  initial?.roleUuid ? 'Edit role' : 'Create role';

const MODULE = 'employees-roles-editor';

const logEvent = (event: string, fields: Record<string, unknown> = {}): void => {
  logger.info(MODULE, event, { event, ...fields });
};

const RoleEditorModal = ({
  open,
  initial,
  submitting,
  errorMessage,
  onClose,
  onSubmit,
}: RoleEditorModalProps) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [localError, setLocalError] = useState('');

  useEffect(() => {
    if (open) {
      setName(initial?.name || '');
      setDescription(initial?.description || '');
      setLocalError('');
      logEvent('role_editor_open', {
        mode: initial?.roleUuid ? 'edit' : 'create',
        roleUuid: initial?.roleUuid || null,
      });
    }
  }, [open, initial]);

  if (!open) return null;

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    const trimmedName = name.trim();
    if (!trimmedName) {
      setLocalError('Role name is required.');
      logEvent('role_editor_validation_failed', { reason: 'empty_name' });
      return;
    }
    setLocalError('');
    logEvent('role_editor_submit', {
      mode: initial?.roleUuid ? 'edit' : 'create',
      roleUuid: initial?.roleUuid || null,
      name: trimmedName,
    });
    onSubmit({ name: trimmedName, description: description.trim() });
  };

  return (
    <div className="role-editor-overlay" role="dialog" aria-modal="true" aria-label={TITLE_FOR(initial)}>
      <form className="role-editor" onSubmit={handleSubmit}>
        <h3 className="role-editor__title">{TITLE_FOR(initial)}</h3>

        <label className="role-editor__field">
          <span className="role-editor__label">Name</span>
          <input
            className="role-editor__input"
            type="text"
            maxLength={60}
            value={name}
            onChange={(e) => setName(e.target.value)}
            autoFocus
            disabled={submitting}
            required
          />
        </label>

        <label className="role-editor__field">
          <span className="role-editor__label">Description (optional)</span>
          <textarea
            className="role-editor__textarea"
            maxLength={500}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            disabled={submitting}
          />
        </label>

        {(localError || errorMessage) && (
          <p className="role-editor__error" role="alert">
            {localError || errorMessage}
          </p>
        )}

        <div className="role-editor__actions">
          <button
            type="button"
            className="role-editor__cancel"
            onClick={() => {
              logEvent('role_editor_cancel', { roleUuid: initial?.roleUuid || null });
              onClose();
            }}
            disabled={submitting}
          >
            Cancel
          </button>
          <button type="submit" className="role-editor__submit" disabled={submitting}>
            {submitting ? 'Saving…' : initial?.roleUuid ? 'Save changes' : 'Create role'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default RoleEditorModal;
